import { zodResolver } from "@hookform/resolvers/zod";
import { Folder, X } from "lucide-react";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";

import { useAuth, useNavigation } from "@/providers";
import { RobotoButton } from "@/shared/components";
import { CustomFieldValuesSection } from "@/shared/components/CustomFields";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { CollectionResourceType } from "@/shared/domain/collections";
import { useCreateCollection } from "@/shared/domain/collections/hooks/useCreateCollection";
import { TargetEntityType } from "@/shared/domain/customFields";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { TagInput, Textarea, TextInput } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";

import {
  CollectionFormValues,
  collectionFormSchema,
} from "./collectionFormSchema";
import styles from "./CreateCollection.module.css";

// ── constants ─────────────────────────────────────────────────────────────────

const BASE_RESOURCE_TYPES: CollectionResourceType[] = [
  "file",
  "dataset",
  "event",
];

// ── props ─────────────────────────────────────────────────────────────────────

export interface CreateCollectionProps {
  preselectedType?: CollectionResourceType;
  preselectedResources?: Array<{
    resource_type: CollectionResourceType;
    resource_id: string;
  }>;
  close: () => void;
}

// ── component ─────────────────────────────────────────────────────────────────

export const CreateCollection: React.FC<CreateCollectionProps> = ({
  preselectedType = "file",
  preselectedResources,
  close,
}) => {
  const { currentOrganization } = useAuth();
  const { goto } = useNavigation();
  const showNotification = useNotification();
  const { mutate: createCollection, isPending, error } = useCreateCollection();
  const sessionsEnabled = useCheckFeatureFlag("release-sessions");
  const resourceTypes: CollectionResourceType[] = sessionsEnabled
    ? [...BASE_RESOURCE_TYPES, "session"]
    : BASE_RESOURCE_TYPES;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CollectionFormValues>({
    resolver: zodResolver(collectionFormSchema),
    defaultValues: {
      name: "",
      description: "",
      tags: [],
      resourceType: preselectedType,
    },
  });

  const [customFieldValues, setCustomFieldValues] = React.useState<
    Record<string, unknown>
  >({});

  const onSubmit = (values: CollectionFormValues) => {
    if (!currentOrganization?.org_id) {
      return;
    }

    createCollection(
      {
        orgId: currentOrganization.org_id,
        name: values.name,
        description: values.description || undefined,
        tags: values.tags.length ? values.tags : undefined,
        resourceType: values.resourceType,
        resources: preselectedResources,
        customFields:
          Object.keys(customFieldValues).length > 0
            ? customFieldValues
            : undefined,
      },
      {
        onSuccess: (record) => {
          close();
          if (!preselectedResources) {
            goto.collection({ collectionId: record.collection_id });
          } else {
            showNotification("Collection created");
          }
        },
      },
    );
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.title}>
          <Folder size={14} /> New collection
        </div>
        <button className={styles.closeBtn} onClick={close} aria-label="Close">
          <X size={14} />
        </button>
      </div>

      <form
        className={styles.form}
        onSubmit={(e) => void handleSubmit(onSubmit)(e)}
      >
        {error && (
          <div className={styles.errorBanner}>
            {userFacingErrorMessage(error)}
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            Name<span className={styles.req}>*</span>
          </label>
          <TextInput
            {...register("name")}
            autoFocus
            autoComplete="off"
            placeholder="e.g. Urban edge cases"
          />
          {errors.name && (
            <span className={styles.fieldError}>{errors.name.message}</span>
          )}
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>Description</label>
          <Textarea
            {...register("description")}
            autoComplete="off"
            placeholder="What is this collection for?"
          />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>Entity type</label>
          <Controller
            control={control}
            name="resourceType"
            render={({ field }) => (
              <div className={styles.typeSelector}>
                {resourceTypes.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={
                      field.value === t
                        ? `${styles.typeBtn} ${styles.typeBtnActive}`
                        : styles.typeBtn
                    }
                    onClick={() => field.onChange(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>Tags</label>
          <Controller
            control={control}
            name="tags"
            render={({ field }) => (
              <TagInput
                initialTags={field.value}
                onChange={field.onChange}
                aria-label="Tags"
              />
            )}
          />
        </div>

        {currentOrganization?.org_id && (
          <CustomFieldValuesSection
            entityType={TargetEntityType.Collection}
            orgId={currentOrganization.org_id}
            values={customFieldValues}
            onChange={setCustomFieldValues}
            header={<label className={styles.fieldLabel}>Custom fields</label>}
          />
        )}

        <div className={styles.footer}>
          <RobotoButton
            type="button"
            variant="text"
            onClick={close}
            eventName="NewCollectionCancelClicked"
          >
            Cancel
          </RobotoButton>
          <RobotoButton
            type="submit"
            variant="contained"
            color="secondary"
            disabled={isPending}
            eventName="NewCollectionSubmitClicked"
          >
            {isPending ? "Creating…" : "Create collection"}
          </RobotoButton>
        </div>
      </form>
    </div>
  );
};
